import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '../types';
import {
  InvoiceInterface,
  InvoiceItemsInterface,
  DeleteInvoicesInterface
} from '@interfaces/invoice.interface';
import { AxiosPromise } from 'axios';

const url = '/simple-invoices';

export class ApiInvoice extends BaseApiClient {
  /**
   * Gets list invoice
   */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<InvoiceInterface>> {
    // ^^ TODO - invoice: // needs types changing and filters added maybe
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.q = searchTerm;
      }
      this.client
        .getList(url, listData)
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta?.current_page,
                perPage: res.data.meta?.per_page,
                total: res.data.meta?.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * Get a task
   */
  async get(id: number): Promise<InvoiceInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`${url}/${id}`)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async cancel(id: number) {
    return this.client.put(url, { data: [{ id }] });
  }

  /**
   * Delete a invoice
   */
  async delete(id: number) {
    return this.client.delete(`${url}/${id}`);
  }

  async deleteInvoices(IDs: DeleteInvoicesInterface) {
    return new Promise((resolve, reject) => {
      this.client
        .delete(url, {
          data: IDs
        })
        .then((res) => resolve(res))
        .catch((err) => reject(err));
    });
  }

  /**
   * Delete a task collection
   */
  async deleteMultiple(ids: number[]) {
    return this.client.delete(url, {
      data: { data: ids.map((value: number) => ({ id: value })) }
    });
  }

  postStatusSent(id: number): AxiosPromise<InvoiceInterface> {
    return new Promise((resolve) => {
      this.client
        .post(`${url}/${id}/mark-as-sent`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postStatusPaid(
    id: number,
    status: 'paid' | 'unpaid'
  ): Promise<InvoiceInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .post(`${url}/${id}/paid-status`, {
          paid: status
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }
  // will need to be removed when BE applies the above
  postStatusPaidOld(id: number): AxiosPromise<InvoiceInterface> {
    return new Promise((resolve) => {
      this.client
        .post(`${url}/${id}/paid-status`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postStatusApproved(id: number): AxiosPromise<InvoiceInterface> {
    return new Promise((resolve) => {
      this.client
        .post(`${url}/${id}/mark-as-approved`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  /**
   * Store a invoice
   */
  async store(body: Partial<InvoiceInterface>): Promise<InvoiceInterface> {
    // TODO
    return new Promise((resolve, reject) => {
      this.client
        .post(url, body)
        .then((res) => {
          if (res.data) {
            resolve(res.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  put(
    id: number,
    body: Partial<InvoiceInterface>
  ): AxiosPromise<InvoiceInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`${url}/${id}`, body)
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  deleteQuote(id: number): AxiosPromise<true> {
    return new Promise((resolve) => {
      this.client
        .delete(`${url}?invoice_ids=${id}`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }

  postSendToClient(
    quoteId: number,
    body: {
      to: string[];
      cc: string[];
      subject: string;
      message: string;
    }
  ) {
    return new Promise((resolve, reject) => {
      this.client
        .post(`${url}/${quoteId}/send`, body)
        .then((res) => {
          if (res.status === 422) throw res;
          return resolve(res);
        })
        .catch((err) => reject(err));
    });
  }

  storeDocument(
    quoteId: number,
    files: Array<File>
  ): Promise<InvoiceInterface> {
    return new Promise((resolve) => {
      const fd = new FormData();

      files.forEach((file) => {
        fd.append('files[]', file, file.name);
      });

      this.client
        .post(`${url}/${quoteId}/documents`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  deleteDocuments(quoteId: number, id: number[]): Promise<InvoiceInterface> {
    return new Promise((resolve) => {
      const ids = id.reduce((prev, cur) => prev + cur + ',', '');
      this.client
        .delete(
          `${url}/${quoteId}/documents?document_ids=${ids.slice(
            0,
            ids.length - 1
          )}`
        )
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }
  // -------- Items ---

  deleteItems(quoteId: number, id: number): AxiosPromise<true> {
    return new Promise((resolve) => {
      this.client
        .delete(`${url}/${quoteId}/items?item_ids=${id}`)
        .then((res) => resolve(res))
        .catch(console.error);
    });
  }
  putItem(
    quoteId: number,
    id: number,
    body: Partial<InvoiceItemsInterface>
  ): AxiosPromise<InvoiceInterface> {
    return new Promise((resolve) => {
      this.client
        .put(`${url}/${quoteId}/items/${id}`, body)
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }
  postAddItem(quoteId: number, body: Partial<InvoiceItemsInterface>) {
    return new Promise((resolve) => {
      this.client
        .post(`${url}/${quoteId}/items`, body)
        .then((res) => resolve(res.data))
        .catch(console.error);
    });
  }

  async exportPdf(
    category: string,
    invoiceId: string,
    paymentId?: string
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/export-pdf/${category}/${invoiceId}/${paymentId}`, {
          responseType: 'blob',
          headers: { Accept: 'application/pdf' }
        })
        .then((res) => {
          if (res.data) {
            resolve(
              URL.createObjectURL(
                new Blob([res.data], { type: 'application/pdf' })
              )
            );
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}

export { invoiceQueryKeys } from './query-keys';
export type {
  InvoiceListQueryParams,
  InvoicePdfExportQueryParams
} from './query-keys';
export {
  useInvoiceListQuery,
  useInvoiceQuery,
  useInvoicePdfExportQuery
} from './use-invoice-queries';
export {
  useInvoiceCancelMutation,
  useInvoiceDeleteMutation,
  useInvoiceDeleteInvoicesMutation,
  useInvoiceDeleteMultipleMutation,
  useInvoicePostStatusSentMutation,
  useInvoicePostStatusPaidMutation,
  useInvoicePostStatusPaidOldMutation,
  useInvoicePostStatusApprovedMutation,
  useInvoiceStoreMutation,
  useInvoicePutMutation,
  useInvoiceDeleteQuoteMutation,
  useInvoicePostSendToClientMutation,
  useInvoiceStoreDocumentMutation,
  useInvoiceDeleteDocumentsMutation,
  useInvoiceDeleteItemsMutation,
  useInvoicePutItemMutation,
  useInvoicePostAddItemMutation
} from './use-invoice-mutations';
export type {
  InvoicePutVariables,
  InvoiceSendToClientVariables,
  InvoiceStoreDocumentVariables,
  InvoiceDeleteDocumentsVariables,
  InvoiceDeleteItemsVariables,
  InvoicePutItemVariables,
  InvoicePostAddItemVariables
} from './use-invoice-mutations';
